import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import copy from 'copy-to-clipboard';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {Add, ContentCopy, Delete, Refresh, Visibility, VisibilityOff} from '@mui/icons-material';
import dayjs from 'dayjs';
import {useApplicationContext} from 'ApplicationContext';
import {ApiKeyDto, CreateApiKeyResponse} from 'apiClient/Api';

type KeyStatus = 'active' | 'expired' | 'revoked';

const keyStatus = (key: ApiKeyDto): KeyStatus => {
    if (key.revokedAt) return 'revoked';
    if (key.expiresAt && dayjs(key.expiresAt).isBefore(dayjs())) return 'expired';
    return 'active';
};

const statusChipColor = (status: KeyStatus): 'success' | 'warning' | 'default' => {
    switch (status) {
        case 'active': return 'success';
        case 'expired': return 'warning';
        case 'revoked': return 'default';
    }
};

const ApiKeysList: React.FC = () => {
    const {t} = useTranslation();
    const context = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();

    const [keys, setKeys] = useState<ApiKeyDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [createOpen, setCreateOpen] = useState(false);
    const [createName, setCreateName] = useState('');
    const [createNeverExpires, setCreateNeverExpires] = useState(false);
    const [createExpiresAt, setCreateExpiresAt] = useState('');
    const [creating, setCreating] = useState(false);

    const [revealedKey, setRevealedKey] = useState<CreateApiKeyResponse | null>(null);
    const [revealedVisible, setRevealedVisible] = useState(false);

    const fetchKeys = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await context.api.apiKeys.apiKeysList();
            setKeys(response.data ?? []);
        } catch {
            setError(t('apiKeys.loadError'));
        } finally {
            setLoading(false);
        }
    }, [context.api.apiKeys, t]);

    useEffect(() => { fetchKeys(); }, [fetchKeys]);

    const openCreateDialog = () => {
        setCreateName('');
        setCreateNeverExpires(false);
        setCreateExpiresAt('');
        setCreateOpen(true);
    };

    const submitCreate = async () => {
        if (!createName.trim()) {
            enqueueSnackbar(t('apiKeys.create.nameRequired'), {variant: 'warning'});
            return;
        }
        setCreating(true);
        try {
            const response = await context.api.apiKeys.apiKeysCreate({
                name: createName.trim(),
                neverExpires: createNeverExpires,
                expiresAt: createNeverExpires
                    ? null
                    : (createExpiresAt ? dayjs(createExpiresAt).toISOString() : null),
            });
            setCreateOpen(false);
            setRevealedKey(response.data);
            setRevealedVisible(false);
            await fetchKeys();
        } catch (e: any) {
            const msg = e?.response?.data?.detail ?? e?.message ?? t('apiKeys.create.error');
            enqueueSnackbar(msg, {variant: 'error'});
        } finally {
            setCreating(false);
        }
    };

    const handleRevoke = async (key: ApiKeyDto) => {
        if (!key.id) return;
        if (!window.confirm(t('apiKeys.revoke.confirm', {name: key.name ?? '-'}))) return;
        try {
            await context.api.apiKeys.apiKeysRevoke(key.id);
            enqueueSnackbar(t('apiKeys.revoke.success'), {variant: 'success'});
            await fetchKeys();
        } catch {
            enqueueSnackbar(t('apiKeys.revoke.error'), {variant: 'error'});
        }
    };

    const copyToClipboard = (value: string) => {
        if (copy(value)) {
            enqueueSnackbar(t('apiKeys.copied'), {variant: 'success'});
        }
    };

    return (
        <Box>
            <Stack
                direction={{xs: 'column', sm: 'row'}}
                spacing={2}
                sx={{alignItems: {xs: 'stretch', sm: 'center'}, justifyContent: 'space-between', mb: 3}}
            >
                <Box>
                    <Typography variant="h5" component="h2">{t('apiKeys.title')}</Typography>
                    <Typography variant="body2" color="text.secondary">{t('apiKeys.subtitle')}</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Tooltip title={t('apiKeys.refresh')}>
                        <span>
                            <IconButton onClick={fetchKeys} disabled={loading}>
                                {loading ? <CircularProgress size={20}/> : <Refresh/>}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Button variant="contained" startIcon={<Add/>} onClick={openCreateDialog}>
                        {t('apiKeys.create.button')}
                    </Button>
                </Stack>
            </Stack>

            <Alert severity="info" sx={{mb: 2}}>{t('apiKeys.warningOnce')}</Alert>

            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

            <Card variant="outlined">
                <CardContent sx={{p: 0, '&:last-child': {pb: 0}}}>
                    {loading && keys.length === 0 ? (
                        <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                            <CircularProgress/>
                        </Box>
                    ) : keys.length === 0 ? (
                        <Box sx={{textAlign: 'center', py: 6}}>
                            <Typography color="text.secondary">{t('apiKeys.empty')}</Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('apiKeys.columns.name')}</TableCell>
                                        <TableCell>{t('apiKeys.columns.prefix')}</TableCell>
                                        <TableCell>{t('apiKeys.columns.status')}</TableCell>
                                        <TableCell>{t('apiKeys.columns.created')}</TableCell>
                                        <TableCell>{t('apiKeys.columns.lastUsed')}</TableCell>
                                        <TableCell>{t('apiKeys.columns.expires')}</TableCell>
                                        <TableCell align="right">{t('apiKeys.columns.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {keys.map(key => {
                                        const status = keyStatus(key);
                                        return (
                                            <TableRow key={key.id} hover>
                                                <TableCell sx={{fontWeight: 500}}>{key.name ?? '-'}</TableCell>
                                                <TableCell sx={{fontFamily: 'monospace', fontSize: '0.85rem'}}>
                                                    {key.keyPrefix ? `${key.keyPrefix}…` : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={t(`apiKeys.status.${status}`)}
                                                        color={statusChipColor(status)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {key.createdAt ? dayjs(key.createdAt).format('DD.MM.YYYY') : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {key.lastUsedAt
                                                        ? dayjs(key.lastUsedAt).format('DD.MM.YYYY HH:mm')
                                                        : <Typography variant="caption" color="text.secondary">{t('apiKeys.neverUsed')}</Typography>}
                                                </TableCell>
                                                <TableCell>
                                                    {key.expiresAt
                                                        ? dayjs(key.expiresAt).format('DD.MM.YYYY')
                                                        : <Typography variant="caption" color="text.secondary">{t('apiKeys.neverExpires')}</Typography>}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {status !== 'revoked' && (
                                                        <Tooltip title={t('apiKeys.revoke.button')}>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleRevoke(key)}
                                                            >
                                                                <Delete fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('apiKeys.create.title')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <DialogContentText>{t('apiKeys.create.description')}</DialogContentText>
                        <TextField
                            autoFocus
                            label={t('apiKeys.create.nameLabel')}
                            value={createName}
                            onChange={(e) => setCreateName(e.target.value)}
                            placeholder={t('apiKeys.create.namePlaceholder')}
                            fullWidth
                            required
                            disabled={creating}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={createNeverExpires}
                                    onChange={(_, checked) => setCreateNeverExpires(checked)}
                                    disabled={creating}
                                />
                            }
                            label={t('apiKeys.create.neverExpiresLabel')}
                        />
                        <TextField
                            type="date"
                            label={t('apiKeys.create.expiresAtLabel')}
                            value={createExpiresAt}
                            onChange={(e) => setCreateExpiresAt(e.target.value)}
                            disabled={createNeverExpires || creating}
                            fullWidth
                            slotProps={{inputLabel: {shrink: true}}}
                            helperText={createNeverExpires ? t('apiKeys.create.expiresDisabledHelper') : t('apiKeys.create.expiresHelper')}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)} disabled={creating}>
                        {t('apiKeys.create.cancel')}
                    </Button>
                    <Button
                        onClick={submitCreate}
                        variant="contained"
                        disabled={creating || !createName.trim()}
                        startIcon={creating ? <CircularProgress size={16}/> : null}
                    >
                        {t('apiKeys.create.submit')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!revealedKey} onClose={() => setRevealedKey(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('apiKeys.reveal.title')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <Alert severity="warning">{t('apiKeys.reveal.warning')}</Alert>
                        <TextField
                            label={revealedKey?.name ?? t('apiKeys.columns.name')}
                            value={revealedKey?.key ?? ''}
                            type={revealedVisible ? 'text' : 'password'}
                            fullWidth
                            slotProps={{
                                input: {
                                    readOnly: true,
                                    sx: {fontFamily: 'monospace'},
                                    endAdornment: (
                                        <Stack direction="row">
                                            <Tooltip title={revealedVisible ? t('apiKeys.reveal.hide') : t('apiKeys.reveal.show')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setRevealedVisible(v => !v)}
                                                >
                                                    {revealedVisible
                                                        ? <VisibilityOff fontSize="small"/>
                                                        : <Visibility fontSize="small"/>}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t('apiKeys.reveal.copy')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => revealedKey?.key && copyToClipboard(revealedKey.key)}
                                                >
                                                    <ContentCopy fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    ),
                                },
                            }}
                        />
                        {revealedKey?.expiresAt && (
                            <Typography variant="caption" color="text.secondary">
                                {t('apiKeys.reveal.expiresOn', {date: dayjs(revealedKey.expiresAt).format('DD.MM.YYYY')})}
                            </Typography>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevealedKey(null)} variant="contained">
                        {t('apiKeys.reveal.done')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ApiKeysList;
